import ApiService from './ApiService';
import { BehaviorSubject } from 'rxjs';
import { Dispatch, SetStateAction } from 'react';
import { UnitDefinition } from '../models/Api';

const initialState: UnitDefinition[] = [];

const state$ = new BehaviorSubject<UnitDefinition[]>(initialState);

const setState = (data: UnitDefinition[]) => {
  return state$.next([...data]);
};

const UnitService = {
  fetchUnits: async () => {
    let unitDefinitions = [] as UnitDefinition[];
    const api = ApiService.getApi();
    const response = await api.supportDataList();
    if (response.status === 200) {
      unitDefinitions = response.data?.units ?? unitDefinitions;
    }
    setState(unitDefinitions);
  },

  getState: (setUnitsData: Dispatch<SetStateAction<UnitDefinition[]>>) => {
    return state$.asObservable().subscribe((next) => setUnitsData(next));
  },

  getCurrentState(): UnitDefinition[] {
    return state$.value;
  },

  getFilteredUnits: (unitGroup: string, unitSubGroup?: number) => {
    const filteredUnits = state$.value.filter((unitDefinition) => unitDefinition.unitGroup === unitGroup && unitDefinition.unitSubgroup === unitSubGroup);
    return filteredUnits;
  },

  getEnumForUnitName: <T extends Record<string, string>>(unitName: string, enumeration: T) => {
    const enumName = (Object.keys(enumeration) as Array<keyof T>).find((key) => enumeration[key] === unitName);

    if (!enumName) throw Error(`The value ${unitName} is not part of the enum ${enumeration}`);
    return enumeration[enumName];
  },

  getLabelForUnitName: (unitName: string | undefined, unitDefinitions: UnitDefinition[]) => {
    return unitDefinitions.find((unit) => unit.unitName === unitName)?.unitLabel;
  },

  getDefaultUnitForSystemOfMeasurement: <T extends Record<string, string>>(isMetric: boolean, unitGroup: string, enumeration: T) => {
    const defaultUnitForSystemOfMeasurement = state$.value
      .filter((unitDefinition) => unitDefinition.unitGroup === unitGroup)
      .find((unit) => (isMetric ? unit.isDefaultForMetric : unit.isDefaultForImperial));
    const defaultUnitDefinition = defaultUnitForSystemOfMeasurement as UnitDefinition;
    const unitName = defaultUnitDefinition.unitName as string;
    const enumName = (Object.keys(enumeration) as Array<keyof T>).find((key) => enumeration[key] === unitName);
    if (!enumName) throw Error(`The value ${unitName} is not part of the enum ${enumeration}`);
    return enumeration[enumName];
  },

  getUnitDefinitionByGroupAndName: (group: string, name: string) => {
    const filteredUnits = state$.value.find((unitDefinition) => unitDefinition.unitGroup === group && unitDefinition.unitName === name);
    return filteredUnits;
  },
};

export default UnitService;
